import {
  DollarSign,
  Users,
  TrendingUp,
  Clock,
  BarChart,
  Zap,
  Globe,
  Shield,
} from "lucide-react";

export const whyChooseUs = [
  {
    icon: Shield,
    title: "Secure Streaming",
    description: "Enterprise-grade encryption for all your content delivery.",
  },
  {
    icon: Zap,
    title: "Lightning Fast",
    description: "Buffer-free streaming with our optimized CDN network.",
  },
  {
    icon: Globe,
    title: "Global Coverage",
    description: "Reach audiences worldwide with 99.9% uptime guarantee.",
  },
];

export const pricingPlansMain = [
  {
    id: 1,
    title: "Prime Test",
    description: "Test your device with our service",
    price: "Free",
    features: [
      { text: "Unlimited streaming", included: true },
      { text: "HD quality", included: true },
      { text: "24/7 support", included: true },
      {
        text: "Adult content",
        included: false,
        note: "not included in the free plan",
      },
    ],
    cta: "Get Free",
    ctaLink: "/plans",
  },
  {
    id: 2,
    title: "Start Stream",
    description: "1 Month Access",
    price: "€10.00",
    features: [
      { text: "Full channel lineup", included: true },
      { text: "Movies & Series", included: true },
      { text: "HD quality", included: true },
      { text: "24/7 support", included: true },
      {
        text: "Adult content",
        included: true,
        note: "available on request — protected with PIN",
      },
    ],
    cta: "Get Started",
    ctaLink: "/plans",
  },
  {
    id: 3,
    title: "Power Stream",
    description: "3 Months Access",
    price: "€25.00",
    features: [
      { text: "Full channel lineup", included: true },
      { text: "Movies & Series", included: true },
      { text: "HD quality", included: true },
      { text: "24/7 support", included: true },
      { text: "Best value for new users", included: true },
      {
        text: "Adult content",
        included: true,
        note: "available on request — protected with PIN",
      },
    ],
    cta: "Choose Plan",
    ctaLink: "/plans",
  },
  {
    id: 4,
    title: "Pro Stream",
    description: "6 Months Access",
    price: "€40.00",
    features: [
      { text: "Full channel lineup", included: true },
      { text: "Movies & Series", included: true },
      { text: "HD quality", included: true },
      { text: "24/7 support", included: true },
      { text: "Perfect for longterm users", included: true },
      { text: "Free 6 months VPN", included: true },
      {
        text: "Adult content",
        included: true,
        note: "available on request — protected with PIN",
      },
    ],
    cta: "Select Plan",
    ctaLink: "/plans",
  },
  {
    id: 5,
    title: "Ultra Stream",
    description: "12 Months Access",
    price: "€70.00",
    features: [
      { text: "Full channel lineup", included: true },
      { text: "Movies & Series", included: true },
      { text: "HD quality", included: true },
      { text: "24/7 support", included: true },
      { text: "Maximum savings — most popular", included: true },
      { text: "Free 12 months VPN", included: true },
      {
        text: "Adult content",
        included: true,
        note: "available on request — protected with PIN",
      },
    ],
    cta: "Go Premium",
    ctaLink: "/plans",
  },
];

export const resellerPlans = [
  {
    id: 1,
    name: "Starter",
    description: "Reseller Start",
    price: "€250",
    credits: "120",
    creditPrice: "€2.08",
    popular: false,
    badge: null,
    cta: "Get Started",
    ctaLink: "/apply?plan=starter",
  },
  {
    id: 2,
    name: "Accelerator",
    description: "Reseller Growth",
    price: "€490",
    credits: "240",
    creditPrice: "€2.04",
    popular: false,
    badge: null,
    cta: "Get Started",
    ctaLink: "/apply?plan=accelerator",
  },
  {
    id: 3,
    name: "High Volume",
    description: "Reseller Pro",
    price: "€720",
    credits: "360",
    creditPrice: "€2.00",
    popular: true,
    badge: "MOST POPULAR",
    cta: "Get Started",
    ctaLink: "/apply?plan=high-volume",
  },
  {
    id: 4,
    name: "Enterprise",
    description: "Reseller Elite",
    price: "€900",
    credits: "480",
    creditPrice: "€1.87",
    popular: false,
    badge: "ENTERPRISE READY",
    cta: "Get Started",
    ctaLink: "/apply?plan=enterprise",
  },
];

export const affiliateLevels = [
  {
    id: 1,
    name: "Start Stream",
    rate: "10%",
    requirements: "1 month",
    description: "Perfect for beginners",
    popular: false,
  },
  {
    id: 2,
    name: "Power Stream",
    rate: "15%",
    requirements: "3 months",
    description: "Consistent promoters",
    popular: false,
  },
  {
    id: 3,
    name: "Pro Stream",
    rate: "20%",
    requirements: "6 months",
    description: "Experienced affiliates",
    popular: false,
  },
  {
    id: 4,
    name: "Ultra Stream",
    rate: "25%",
    requirements: "12 months",
    description: "Top performers",
    popular: true,
  },
];

export const affiliateBenefits = [
  {
    id: 1,
    icon: DollarSign,
    title: "Recurring Commissions",
    description:
      "Earn 10-25% on every renewal the customer makes for the entire lifetime of their subscription.",
  },
  {
    id: 2,
    icon: TrendingUp,
    title: "Tiered Rates",
    description:
      "Higher commissions based on duration. Earn 25% for an annual subscription.",
  },
  {
    id: 4,
    icon: Clock,
    title: "30 Day Update",
    description:
      "Get instant updates on every new subscription or renewal. Get detailed updates for the last month.",
  },
  {
    id: 5,
    icon: BarChart,
    title: "Monthly Payouts",
    description:
      "Receive your payments, by the agreed method, on the 1st of each month.",
  },
  {
    id: 6,
    icon: Users,
    title: "Dedicated Manager",
    description: "Get a personal affiliate manager.",
  },
];

export const featuresSlideshow = [
  {
    id: 1,
    title: "4K Streaming",
    content: (
      <div className="space-y-4">
        <p className="font-body text-muted-foreground">
          <span className="font-bold text-foreground">
            Crystal‑Clear Quality on Any Device
          </span>
          <br />
          Subscribers enjoy ultra‑sharp 4K resolution with vibrant colors, deep
          contrast, and smooth motion — perfect for sports, movies, and live
          events. Whether they&apos;re watching on a big‑screen TV or a mobile
          device, the picture stays crisp and detailed.
        </p>
        <div>
          <p className="font-bold text-foreground mb-2">
            Premium Sound Experience
          </p>
          <p className="font-body text-muted-foreground mb-2">
            Great visuals deserve great audio. Your service supports:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li className="font-body text-muted-foreground">
              Dolby‑level surround sound (where available)
            </li>
            <li className="font-body text-muted-foreground">
              Rich, immersive audio for movies and sports
            </li>
            <li className="font-body text-muted-foreground">
              Clear dialogue and balanced sound even on smaller devices
            </li>
            <li className="font-body text-muted-foreground">
              Full compatibility with soundbars, home theaters, and Bluetooth
              speakers
            </li>
          </ul>
          <p className="font-body text-muted-foreground mt-2">
            This combination of 4K picture + high‑quality sound gives
            subscribers a true real experience at home or on the go.
          </p>
        </div>
      </div>
    ),
    image: "/feature1.png",
  },
  {
    id: 2,
    title: "Multi-Device Support",
    content: (
      <div className="space-y-4">
        <p className="font-body text-muted-foreground">
          Available on all major platforms — stream via your favorite device.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {[
            "Android Box",
            "Android Smart TV",
            "Apple TV",
            "SAMSUNG",
            "LG SMART TV",
            "FIRE TV",
            "ROKU",
            "VIZIO",
            "PlayStation",
            "Xbox",
            "Nintendo",
            "MAG",
          ].map((device) => (
            <div
              key={device}
              className="bg-muted/50 rounded-lg px-3 py-2 text-center"
            >
              <span className="font-body text-xs text-foreground">
                {device}
              </span>
            </div>
          ))}
        </div>
      </div>
    ),
    image: "/feature2.png",
  },
  {
    id: 3,
    title: "24/7 Support",
    content: (
      <div className="space-y-4">
        <p className="font-body text-muted-foreground">
          <span className="font-bold text-foreground">
            Always here when you need us
          </span>
        </p>
        <div>
          <p className="font-bold text-foreground mb-2">
            Why it matters to us:
          </p>
          <ul className="list-disc pl-5 space-y-2">
            <li className="font-body text-muted-foreground">
              <span className="font-medium text-foreground">
                Instant help, anytime
              </span>{" "}
              — no waiting for business hours
            </li>
            <li className="font-body text-muted-foreground">
              <span className="font-medium text-foreground">
                Global coverage
              </span>{" "}
              — because streaming knows no borders
            </li>
            <li className="font-body text-muted-foreground">
              <span className="font-medium text-foreground">
                We strive for your highest satisfaction
              </span>{" "}
              — Enjoy care and support
            </li>
            <li className="font-body text-muted-foreground">
              <span className="font-medium text-foreground">
                Faster problem resolution
              </span>{" "}
              — less delays, more time enjoying
            </li>
            <li className="font-body text-muted-foreground">
              <span className="font-medium text-foreground">
                We invest in trust
              </span>{" "}
              — We are committed to reliability and service
            </li>
          </ul>
        </div>
      </div>
    ),
    image: "/feature3.png",
  },
];

export const faq = [
  {
    id: 1,
    question: "Can I resell your IPTV service?",
    content: (
      <div className="space-y-4 overflow-x-auto">
        <p>
          Yes — we offer a Reseller Program for users who want to start their
          own IPTV business or add IPTV to their existing services.
        </p>
        <div>
          <p className="font-semibold text-foreground mb-2">What you get:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Reseller panel access</li>
            <li>Create/manage customer accounts</li>
            <li>Full control over pricing</li>
            <li>Realtime activation management</li>
            <li>Credits that never expire</li>
            <li>24/7 reseller support</li>
          </ul>
        </div>
        <div>
          <p className="font-semibold text-foreground mb-2">How it works:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Purchase reseller credits</li>
            <li>Receive panel login</li>
            <li>Create customer lines anytime</li>
            <li>Set your own prices and keep 100% profit</li>
          </ul>
        </div>
      </div>
    ),
  },
  {
    id: 2,
    question: "Are refunds available?",
    content: (
      <div className="space-y-4">
        <p>
          Refunds are not available once a subscription or trial has been
          activated because digital access cannot be returned.
        </p>
        <div>
          <p className="font-semibold text-foreground mb-2">Important notes:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Test with a free trial first</li>
            <li>Ensure your device and internet are compatible</li>
            <li>Support will help fix any issues</li>
          </ul>
        </div>
      </div>
    ),
  },
  {
    id: 3,
    question: "How long does it take to activate my subscription?",
    content: (
      <div className="space-y-4">
        <p>Activation is instant — usually 1–5 minutes after payment.</p>
        <div>
          <p className="font-semibold text-foreground mb-2">What to expect:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Login details sent by email/message</li>
            <li>Enter details into your IPTV app</li>
            <li>Channels load immediately</li>
          </ul>
        </div>
      </div>
    ),
  },
  {
    id: 4,
    question: "Can I change from M3U to MAG or vice versa?",
    content: (
      <div className="space-y-4">
        <p>
          Yes — you can switch formats anytime, but it requires a new activation
          line.
        </p>
        <p className="font-semibold text-foreground">Why:</p>
        <p className="text-muted-foreground">
          Each format uses different authentication, so a fresh line must be
          generated.
        </p>
      </div>
    ),
  },
  {
    id: 5,
    question: "How can I contact customer support?",
    content: (
      <div className="space-y-4">
        <p>You can reach support through:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Email</li>
          <li>Live chat</li>
          <li>WhatsApp / Telegram</li>
          <li>Support ticket system</li>
        </ul>
        <div>
          <p className="font-semibold text-foreground mb-2">
            For faster help include:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Order number</li>
            <li>Device type</li>
            <li>Short issue description</li>
          </ul>
        </div>
      </div>
    ),
  },
  {
    id: 6,
    question: "How can I pay for the subscription?",
    content: (
      <div className="space-y-4">
        <p>We accept:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Credit/debit cards</li>
          <li>PayPal (where available)</li>
          <li>Bank transfer</li>
          <li>Cryptocurrency</li>
          <li>Local payment methods (region dependent)</li>
        </ul>
      </div>
    ),
  },
  {
    id: 7,
    question: "What should I do if some channels are freezing or not working?",
    content: (
      <div className="space-y-4">
        <p>Try these steps:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Check your internet (20–30 Mbps recommended)</li>
          <li>Restart your IPTV app</li>
          <li>Try another player (Smarters, TiviMate, etc.)</li>
          <li>Reboot your device</li>
          <li>Use Ethernet instead of WiFi</li>
          <li>Test other channels</li>
          <li>Contact support if the issue continues</li>
        </ul>
      </div>
    ),
  },
  {
    id: 8,
    question: "How many devices can I use with one subscription?",
    content: (
      <p>
        You can install your line on multiple devices, but you can only watch on
        the number of simultaneous connections included in your plan.
      </p>
    ),
  },
  {
    id: 9,
    question: "Can I request specific channels or VOD content?",
    content: (
      <div className="space-y-4">
        <p>Yes — you can request channels, movies, or series.</p>
        <div>
          <p className="font-semibold text-foreground mb-2">Notes:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Not all requests can be guaranteed</li>
            <li>Availability depends on sources</li>
            <li>Most requested content is prioritized</li>
          </ul>
        </div>
      </div>
    ),
  },
  {
    id: 10,
    question:
      "What if I am not satisfied with the service after making the payment?",
    content: (
      <div className="space-y-4">
        <p>Payments are non-refundable once activated, but support will:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Troubleshoot issues</li>
          <li>Optimize your setup</li>
          <li>Refresh or replace your line</li>
          <li>Recommend the best app for your device</li>
        </ul>
      </div>
    ),
  },
  {
    id: 11,
    question: "Do you offer a discount for multiple connections?",
    content: (
      <p>
        Yes — multi-connection plans come with reduced pricing per connection.
      </p>
    ),
  },
  {
    id: 12,
    question: "Which devices work with your service?",
    content: (
      <div className="space-y-4">
        <p>Compatible with:</p>
        <div className="grid grid-cols-2 gap-2">
          <ul className="list-disc pl-5 space-y-1">
            <li>Smart TVs (Samsung, LG, Android TV)</li>
            <li>FireStick / Fire TV</li>
            <li>Android TV Boxes</li>
            <li>NVIDIA Shield</li>
            <li>Chromecast</li>
            <li>Android & iOS devices</li>
          </ul>
          <ul className="list-disc pl-5 space-y-1">
            <li>Windows & Mac</li>
            <li>MAG boxes</li>
            <li>Enigma2 / Dreambox</li>
            <li>Vu+</li>
          </ul>
        </div>
      </div>
    ),
  },
  {
    id: 13,
    question:
      "Does my subscription start to be active right after I make a payment?",
    content: (
      <p>Yes — activation starts immediately after payment confirmation.</p>
    ),
  },
  {
    id: 14,
    question: "Do you have a TV Guide (EPG)?",
    content: (
      <div className="space-y-4">
        <p>Yes — a full EPG is available for most channels.</p>
        <div>
          <p className="font-semibold text-foreground mb-2">Notes:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Some channels may not provide schedule data</li>
            <li>Your app must support EPG</li>
          </ul>
        </div>
      </div>
    ),
  },
  {
    id: 15,
    question:
      "Do you have an official app? Can I use your service in other apps?",
    content: (
      <div className="space-y-4">
        <p>We will have our official app soon.</p>
        <div>
          <p className="font-semibold text-foreground mb-2">
            Yes — you can use any compatible IPTV app:
          </p>
          <div className="grid grid-cols-2 gap-2">
            <ul className="list-disc pl-5 space-y-1">
              <li>IPTV Smarters</li>
              <li>TiviMate</li>
              <li>XCIPTV</li>
              <li>Smart IPTV</li>
              <li>iMPlayer</li>
            </ul>
            <ul className="list-disc pl-5 space-y-1">
              <li>GSE IPTV</li>
              <li>Flix IPTV</li>
              <li>OTT Navigator</li>
            </ul>
          </div>
        </div>
      </div>
    ),
  },
];

export const affiliateHowItWorks = [
  {
    id: 1,
    step: 1,
    title: "Sign Up",
    description: "Join the Affiliate Program",
  },
  {
    id: 2,
    step: 2,
    title: "Promote",
    description: "Get your unique promo id",
  },
  {
    id: 3,
    step: 3,
    title: "Earn",
    description: "15-50% recurring commissions",
  },
  {
    id: 4,
    step: 4,
    title: "Share",
    description: "Share your id to your customer",
  },
  {
    id: 5,
    step: 5,
    title: "Level Up",
    description: "Higher rates every 3 months",
  },
  {
    id: 6,
    step: 6,
    title: "Earn",
    description: "Up to 25% for every subscription (new or renewal)",
  },
];
